<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';

const { contentAttributes, content } = useMessageContext();
const { t } = useI18n();

// MessageList aplica useCamelCase({deep:true}) — tool_name vira toolName.
// Mantém fallback pra snake_case caso algum caller pule a transformação.
const toolName = computed(
  () =>
    contentAttributes.value?.toolName ||
    contentAttributes.value?.tool_name ||
    content.value ||
    ''
);

const actionType = computed(
  () =>
    contentAttributes.value?.actionType ||
    contentAttributes.value?.action_type ||
    ''
);

// Quando é uma intent do kanban_classifier (intent_<N>), o backend grava
// o label resolvido do flow — operador vê "Saudação" em vez de "intent_4"
// cru e tem que abrir o flow builder pra entender.
const intentLabel = computed(
  () =>
    contentAttributes.value?.intentLabel ||
    contentAttributes.value?.intent_label ||
    ''
);

const status = computed(() => contentAttributes.value?.status || 'completed');
const isCalling = computed(() => status.value === 'calling');
// Chamada que NÃO completou pinta a pílula INTEIRA de erro: só o ícone
// vermelho num fundo teal de sucesso, o atendente batia o olho e lia "deu
// certo". O payload da tool não vem pra conversa (retorno livre vira frase
// longa); só o motivo curto que o Python classificou, no tooltip.
const hasFailed = computed(() => status.value === 'failed');
// Slow-path da aba Ferramentas: o turno seguiu e o desfecho chega depois, em
// outra mensagem. Não é concluída nem falha — "aguardando".
const isPending = computed(() => status.value === 'pending');

// O QUE aconteceu na falha (Ai::Actions::Base.tool_call_failure). "Não
// respondeu" só quando ninguém respondeu: a recusa do ERP (conta 393/conv
// 2558) dizia "não respondeu" e mandava investigar conexão em vez do dado.
const failureKind = computed(
  () =>
    contentAttributes.value?.failureKind ||
    contentAttributes.value?.failure_kind ||
    ''
);
const failureReason = computed(
  () =>
    contentAttributes.value?.failureReason ||
    contentAttributes.value?.failure_reason ||
    ''
);

// Cada tipo de ação ganha ícone + paleta própria — separa visualmente
// "IA pediu mais info" de "IA transferiu pra humano" ou "IA resolveu".
// Tom segue a semântica:
//   slate = neutro/info, iris = roteamento interno IA,
//   teal = sucesso/fim, amber = handoff humano, blue = ferramenta externa.
const ACTION_DESCRIPTORS = {
  clarify_intent: { icon: 'i-lucide-help-circle', tone: 'slate' },
  assign_flow: { icon: 'i-lucide-git-branch', tone: 'iris' },
  classify_intent: { icon: 'i-lucide-tag', tone: 'iris' },
  resolve_conversation: { icon: 'i-lucide-check-circle-2', tone: 'teal' },
  transfer_to_team: { icon: 'i-lucide-users-round', tone: 'amber' },
  transfer_to_user: { icon: 'i-lucide-user-round', tone: 'amber' },
  external_tool_call: { icon: 'i-lucide-plug', tone: 'blue' },
  internal_tool_call: { icon: 'i-lucide-database', tone: 'teal' },
  route_to_agent: { icon: 'i-lucide-route', tone: 'iris' },
};
const FALLBACK_DESCRIPTOR = { icon: 'i-lucide-wrench', tone: 'slate' };

// Strings literais por tone — Tailwind purge precisa ver as classes
// inteiras pra mantê-las no bundle final.
const TONE_CLASSES = {
  slate: {
    container:
      'bg-n-slate-3 dark:bg-n-slate-4 border-n-slate-6 text-n-slate-11',
    icon: 'text-n-slate-11',
  },
  iris: {
    container: 'bg-n-iris-3 dark:bg-n-iris-4 border-n-iris-6 text-n-iris-11',
    icon: 'text-n-iris-11',
  },
  teal: {
    container: 'bg-n-teal-3 dark:bg-n-teal-4 border-n-teal-6 text-n-teal-11',
    icon: 'text-n-teal-11',
  },
  amber: {
    container:
      'bg-n-amber-3 dark:bg-n-amber-4 border-n-amber-6 text-n-amber-11',
    icon: 'text-n-amber-11',
  },
  blue: {
    container: 'bg-n-blue-3 dark:bg-n-blue-4 border-n-blue-6 text-n-blue-11',
    icon: 'text-n-blue-11',
  },
};

const FAILED_CLASSES = {
  container: 'bg-n-ruby-3 dark:bg-n-ruby-4 border-n-ruby-8 text-n-ruby-11',
  icon: 'text-n-ruby-11',
};

const descriptor = computed(
  () => ACTION_DESCRIPTORS[actionType.value] || FALLBACK_DESCRIPTOR
);
const toneClasses = computed(() =>
  hasFailed.value ? FAILED_CLASSES : TONE_CLASSES[descriptor.value.tone]
);
const icon = computed(() =>
  hasFailed.value ? 'i-lucide-triangle-alert' : descriptor.value.icon
);

// Switch com keys literais pro extrator i18n rastrear (mesma razão do
// displayLabel abaixo). Tipo desconhecido/ausente (Python antigo) = genérico.
const failureLabel = computed(() => {
  switch (failureKind.value) {
    case 'refused':
      return t('CONVERSATION.AI_TOOL_CALL.FAILURE.REFUSED');
    case 'unavailable':
      return t('CONVERSATION.AI_TOOL_CALL.FAILURE.UNAVAILABLE');
    case 'uncertain':
      return t('CONVERSATION.AI_TOOL_CALL.FAILURE.UNCERTAIN');
    default:
      return t('CONVERSATION.AI_TOOL_CALL.FAILURE.FAILED');
  }
});

const failureExplanation = computed(() => {
  switch (failureKind.value) {
    case 'refused':
      return t('CONVERSATION.AI_TOOL_CALL.FAILURE.REFUSED_TOOLTIP');
    case 'unavailable':
      return t('CONVERSATION.AI_TOOL_CALL.FAILURE.UNAVAILABLE_TOOLTIP');
    case 'uncertain':
      return t('CONVERSATION.AI_TOOL_CALL.FAILURE.UNCERTAIN_TOOLTIP');
    case 'error':
      return t('CONVERSATION.AI_TOOL_CALL.FAILURE.ERROR_TOOLTIP');
    default:
      return t('CONVERSATION.AI_TOOL_CALL.FAILURE.GENERIC_TOOLTIP');
  }
});

const failureTooltip = computed(() => {
  if (!failureReason.value) return failureExplanation.value;
  const reason = t('CONVERSATION.AI_TOOL_CALL.FAILURE.REASON', {
    reason: failureReason.value,
  });
  return `${failureExplanation.value} ${reason}`;
});

const tooltip = computed(() => {
  if (hasFailed.value) return failureTooltip.value;
  if (isPending.value) return t('CONVERSATION.AI_TOOL_CALL.PENDING_TOOLTIP');
  return null;
});

// Prioridade de display:
//   1. intent_label (ex: "Saudação") — mais informativo, vem do flow
//   2. label humanizado do action_type (i18n) — "Pediu mais contexto" etc.
//   3. tool_name cru — fallback pra tools custom não mapeadas
// Switch com keys literais (não template string) pra extrator i18n
// rastrear todas as chaves usadas.
const displayLabel = computed(() => {
  if (intentLabel.value) return intentLabel.value;
  switch (actionType.value) {
    case 'clarify_intent':
      return t('CONVERSATION.AI_TOOL_CALL.ACTIONS.CLARIFY_INTENT');
    case 'assign_flow':
      return t('CONVERSATION.AI_TOOL_CALL.ACTIONS.ASSIGN_FLOW');
    case 'classify_intent':
      return t('CONVERSATION.AI_TOOL_CALL.ACTIONS.CLASSIFY_INTENT');
    case 'resolve_conversation':
      return t('CONVERSATION.AI_TOOL_CALL.ACTIONS.RESOLVE_CONVERSATION');
    case 'transfer_to_team':
      return t('CONVERSATION.AI_TOOL_CALL.ACTIONS.TRANSFER_TO_TEAM');
    case 'transfer_to_user':
      return t('CONVERSATION.AI_TOOL_CALL.ACTIONS.TRANSFER_TO_USER');
    case 'external_tool_call':
      return t('CONVERSATION.AI_TOOL_CALL.ACTIONS.EXTERNAL_TOOL_CALL');
    case 'internal_tool_call':
      return t('CONVERSATION.AI_TOOL_CALL.ACTIONS.INTERNAL_TOOL_CALL');
    case 'route_to_agent':
      return t('CONVERSATION.AI_TOOL_CALL.ACTIONS.ROUTE_TO_AGENT');
    default:
      return toolName.value;
  }
});

// O nome técnico da tool aparece ao lado do label só quando agrega info —
// se for igual (sem ação mapeada e sem intent_label), seria duplicado.
// O nome técnico só aparece quando agrega info real (uma tool de verdade, ex.:
// ixc_buscar_cliente). Quando o tool_name é só o fallback do action_type
// (ex.: "route_to_agent"), mostrar "(route_to_agent)" seria ruído cru.
const showToolName = computed(
  () =>
    toolName.value &&
    toolName.value !== displayLabel.value &&
    toolName.value !== actionType.value
);
const openParen = '(';
const toolNameClose = computed(() => `${toolName.value})`);
</script>

<template>
  <div
    v-tooltip="tooltip"
    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium leading-5"
    :class="[toneClasses.container, { 'border-dashed': isPending }]"
  >
    <span
      v-if="isCalling"
      class="i-lucide-loader-2 size-3.5 animate-spin"
      :class="toneClasses.icon"
    />
    <span v-else class="size-3.5 shrink-0" :class="[icon, toneClasses.icon]" />
    <span class="truncate max-w-[28ch]">{{ displayLabel }}</span>
    <span
      v-if="showToolName"
      class="inline-flex items-center gap-1 font-mono font-normal opacity-70 text-[11px]"
    >
      <span>{{ openParen }}</span>
      <span class="i-lucide-wrench size-3 shrink-0" />
      <span>{{ toolNameClose }}</span>
    </span>
    <span v-if="isCalling" class="opacity-60 font-normal">
      {{ t('CONVERSATION.AI_TOOL_CALL.EXECUTING') }}
    </span>
    <span
      v-if="isPending"
      class="inline-flex items-center gap-1 opacity-70 font-normal"
    >
      <span class="i-lucide-clock size-3 shrink-0" />
      {{ t('CONVERSATION.AI_TOOL_CALL.PENDING') }}
    </span>
    <span v-if="hasFailed" class="font-semibold">
      {{ failureLabel }}
    </span>
  </div>
</template>
